import { useCallback, useEffect, useState } from "react";
import { Download, ListChecks, Plus, RefreshCcw } from "lucide-react";
import AppHeader from "@/components/AppHeader";
import AppSidebar from "@/components/AppSidebar";
import { CeoOverview } from "@/components/tasks/CeoOverview";
import { SprintsPanel, TemplatesPanel } from "@/components/tasks/TaskAdminPanels";
import { TaskCard } from "@/components/tasks/TaskCard";
import { TaskDrawer } from "@/components/tasks/TaskDrawer";
import { TaskForm } from "@/components/tasks/TaskForm";
import { MeetingReport } from "@/components/tasks/MeetingReport";
import { MeetingView, TeamBoard } from "@/components/tasks/TaskViews";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";

const TABS = [
  { key: "my", label: "My Tasks" },
  { key: "board", label: "Team Board" },
  { key: "meeting", label: "Tuesday Meeting" },
  { key: "overview", label: "CEO Overview" },
  { key: "sprints", label: "Sprints" },
  { key: "templates", label: "Recurring" },
];

export default function TasksPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === "system_administrator";
  const canExport = ["system_administrator", "hr"].includes(user?.role);
  const [tab, setTab] = useState("my");
  const [tasks, setTasks] = useState([]);
  const [groups, setGroups] = useState([]);
  const [overview, setOverview] = useState(null);
  const [owners, setOwners] = useState([]);
  const [sprints, setSprints] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [sprintFilter, setSprintFilter] = useState("");
  const [selected, setSelected] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [notice, setNotice] = useState("");

  const load = useCallback(async () => {
    try {
      const params = { include_verified: tab === "board", sprint_id: sprintFilter };
      const [list, meeting, ov, own, spr, tpl] = await Promise.all([
        api.get("/tasks", { params: { ...params, view: tab === "my" ? "my" : "all" } }),
        api.get("/tasks", { params: { view: "meeting" } }),
        api.get("/tasks/overview"),
        api.get("/tasks/owners"),
        api.get("/tasks/sprints"),
        api.get("/tasks/templates"),
      ]);
      setTasks(list.data);
      setGroups(meeting.data);
      setOverview(ov.data);
      setOwners(own.data);
      setSprints(spr.data);
      setTemplates(tpl.data);
      setError("");
      setSelected((current) => (current ? list.data.find((t) => t.id === current.id) || current : null));
    } catch (requestError) {
      setError(formatApiError(requestError));
    } finally {
      setLoading(false);
    }
  }, [tab, sprintFilter]);

  useEffect(() => {
    load();
  }, [load]);

  async function refreshSelected(updated, message) {
    if (message) setNotice(message);
    if (updated) {
      setSelected(updated);
    } else if (selected) {
      try {
        setSelected((await api.get(`/tasks/${selected.id}`)).data);
      } catch {
        setSelected(null);
      }
    }
    await load();
  }

  async function runScheduler() {
    try {
      const response = await api.post("/tasks/scheduler/run");
      const d = response.data;
      setNotice(`Scheduler run: ${d.recurring_created} recurring, ${d.system_created} system task(s) created, ${d.auto_closed} auto-closed, ${d.reminded} reminder(s).`);
      await load();
    } catch (requestError) {
      setError(formatApiError(requestError));
    }
  }

  async function exportCsv() {
    try {
      const response = await api.get("/tasks/export.csv", { responseType: "blob" });
      const url = URL.createObjectURL(response.data);
      const link = document.createElement("a");
      link.href = url;
      link.download = "tagforce-tasks.csv";
      link.click();
      URL.revokeObjectURL(url);
    } catch (requestError) {
      setError(formatApiError(requestError));
    }
  }

  return (
    <div className="app-shell" data-testid="tasks-shell">
      <AppSidebar activeItem="Tasks" />
      <div className="app-content">
        <AppHeader />
        <main className="dashboard-page tasks-page" data-testid="tasks-page">
          <section className="page-intro">
            <div>
              <p className="eyebrow">OPERATIONS</p>
              <h1>Tasks</h1>
              <p className="page-subtitle">Owned work with evidence, CEO verification, recurring templates and system-generated follow-ups.</p>
            </div>
            <div className="button-row">
              {isAdmin && <button type="button" className="button button-ghost" data-testid="tasks-run-scheduler-button" onClick={runScheduler}><RefreshCcw size={15} /> Run scheduler now</button>}
              {canExport && <button type="button" className="button button-secondary" data-testid="tasks-export-button" onClick={exportCsv}><Download size={15} /> Export CSV</button>}
              <button type="button" className="button button-primary" data-testid="tasks-new-button" onClick={() => setShowForm((v) => !v)}><Plus size={15} /> New task</button>
            </div>
          </section>
          {error && <p className="form-message error-message" data-testid="tasks-error">{error}</p>}
          {notice && <p className="form-message success-message" data-testid="tasks-notice">{notice}</p>}
          {showForm && <TaskForm owners={owners} sprints={sprints} onCancel={() => setShowForm(false)} onCreated={(task) => { setShowForm(false); setNotice(`Task created and assigned to ${task.owner_name}.`); load(); }} />}
          <nav className="data-admin-tabs tasks-tabs" data-testid="tasks-tabs">
            {TABS.map((item) => <button key={item.key} type="button" className={tab === item.key ? "active" : ""} data-testid={`tasks-tab-${item.key}`} onClick={() => setTab(item.key)}>{item.label}</button>)}
          </nav>
          {sprintFilter && <p className="pairing-meta" data-testid="tasks-sprint-filter">Filtered by sprint: {sprints.find((s) => s.id === sprintFilter)?.name} · <button type="button" className="subtle-link" data-testid="tasks-clear-sprint" onClick={() => setSprintFilter("")}>clear</button></p>}
          {tab === "my" && (
            <section className="task-list" data-testid="task-my-list">
              <div className="section-heading"><div><h3><ListChecks size={16} /> My tasks</h3><p>{tasks.length} open · overdue items first</p></div></div>
              <div className="task-grid">{tasks.map((task) => <TaskCard key={task.id} task={task} onOpen={setSelected} />)}</div>
              {loading && <p className="operations-empty-state list-loading" data-testid="task-my-loading">Loading…</p>}
              {!loading && !error && !tasks.length && <p className="operations-empty-state" data-testid="task-my-empty">You have no open tasks.</p>}
            </section>
          )}
          {tab === "board" && <TeamBoard tasks={tasks} onOpen={setSelected} />}
          {tab === "meeting" && <MeetingReport onOpen={setSelected} refreshKey={tasks} />}
          {tab === "meeting" && <MeetingView groups={groups} onOpen={setSelected} />}
          {tab === "overview" && <CeoOverview overview={overview} isAdmin={isAdmin} onOpen={setSelected} onChanged={(message) => refreshSelected(null, message)} />}
          {tab === "sprints" && <SprintsPanel sprints={sprints} selected={sprintFilter} onSelect={(id) => { setSprintFilter(id); if (id) setTab("board"); }} onChanged={(message) => refreshSelected(null, message)} />}
          {tab === "templates" && <TemplatesPanel templates={templates} owners={owners} isAdmin={isAdmin} onChanged={(message) => refreshSelected(null, message)} />}
          {selected && <TaskDrawer task={selected} owners={owners} sprints={sprints} user={user} onClose={() => setSelected(null)} onChanged={refreshSelected} />}
        </main>
      </div>
    </div>
  );
}
